/*
 * <store-locator> — mappa dei punti vendita con ricerca, geolocalizzazione,
 * filtro sui bounds e pannello di dettaglio.
 *
 * I negozi arrivano dal data island renderizzato dalla sezione (metafield
 * chalco.stores): i for loop Liquid si fermano a 50 iterazioni e un array da
 * metafield json non e' paginabile, quindi lista e marker si costruiscono qui.
 * Le stringhe restano localizzate: il markup vive nei <template> della sezione e
 * le poche stringhe composte a runtime arrivano come data-i18n-*.
 *
 * Il componente e' uno solo: mappa, lista e ricerca sono controller interni e non
 * custom element, perche' non hanno vita fuori da questo contenitore.
 */
if (!customElements.get('store-locator')) {
  const MAPS_LIBRARIES = 'maps,marker,places';
  const BOUNDS_DEBOUNCE = 120;

  /*
   * Caricamento dell'API Maps.
   *
   * Google raccomanda un bootstrap inline minificato, ma gli script inline sono
   * vietati dal contratto: qui lo script viene creato da JS con loading=async, che
   * evita il blocco del rendering. Il callback deve essere raggiungibile per nome
   * da Google, quindi vive sotto window.theme (unica convenzione globale ammessa)
   * invece di sporcare window.
   */
  const mapsLoader = {
    promise: null,

    load(apiKey) {
      if (this.promise) return this.promise;

      if (window.google && window.google.maps && window.google.maps.importLibrary) {
        this.promise = Promise.resolve();
        return this.promise;
      }

      this.promise = new Promise((resolve, reject) => {
        window.theme = window.theme || {};
        window.theme.__storeLocatorMapsReady = resolve;

        const params = new URLSearchParams({
          key: apiKey,
          v: 'weekly',
          loading: 'async',
          libraries: MAPS_LIBRARIES,
          callback: 'theme.__storeLocatorMapsReady',
        });

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?${params}`;
        script.async = true;
        script.onerror = () => reject(new Error('Google Maps could not be loaded'));
        document.head.appendChild(script);
      });

      return this.promise;
    },
  };

  const DAY_CODES = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

  const toFiniteNumber = (value) => {
    const number = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
    return Number.isFinite(number) ? number : null;
  };

  /* I numeri di telefono sono scritti a mano dal merchant: per href tel: resta
   * solo il prefisso e le cifre. */
  const telHref = (phone) => `tel:${String(phone).replace(/[^\d+]/g, '')}`;

  class StoreLocator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.entries = new Map();
      this.selectedId = null;
      this.detailOpen = false;
      this.lastFocused = null;

      this.map = null;
      this.autocomplete = null;
      this.boundsTimer = null;

      this.elements = {
        search: this.querySelector('[data-store-search]'),
        geolocate: this.querySelector('[data-store-geolocate]'),
        mapHost: this.querySelector('[data-store-map]'),
        list: this.querySelector('[data-store-list]'),
        count: this.querySelector('[data-store-count]'),
        noResults: this.querySelector('[data-store-no-results]'),
        error: this.querySelector('[data-store-error]'),
        results: this.querySelector('.store-locator__results'),
        detail: this.querySelector('[data-store-detail]'),
        cardTemplate: this.querySelector('[data-store-card-template]'),
        detailTemplate: this.querySelector('[data-store-detail-template]'),
        phoneTemplate: this.querySelector('[data-store-phone-template]'),
      };

      this.stores = this.parseStores();
      if (!this.stores.length) {
        this.showError();
        return;
      }

      this.handleKeydown = this.handleKeydown.bind(this);
      document.addEventListener('keydown', this.handleKeydown);

      this.buildList();
      this.start();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.handleKeydown);
      if (this.boundsTimer) clearTimeout(this.boundsTimer);
      this.entries.forEach((entry) => {
        if (entry.marker) entry.marker.map = null;
      });
    }

    /* --- Dato ------------------------------------------------------------- */

    /*
     * Il JSON e' scritto a mano nell'admin: si assume che contenga errori. Un
     * negozio senza nome o senza coordinate valide non e' mappabile e viene
     * scartato invece di far fallire tutto il componente.
     */
    parseStores() {
      const holder = this.querySelector('[data-store-locator-data]');
      if (!holder) return [];

      let parsed;
      try {
        parsed = JSON.parse(holder.textContent);
      } catch (error) {
        return [];
      }

      const list = Array.isArray(parsed) ? parsed : parsed && parsed.stores;
      if (!Array.isArray(list)) return [];

      return list.reduce((stores, raw, index) => {
        if (!raw || typeof raw !== 'object') return stores;

        const lat = toFiniteNumber(raw.lat);
        const lng = toFiniteNumber(raw.lng);
        const name = typeof raw.name === 'string' ? raw.name.trim() : '';
        if (!name || lat === null || lng === null) return stores;

        const phones = Array.isArray(raw.phone)
          ? raw.phone.filter((phone) => typeof phone === 'string' && phone.trim())
          : typeof raw.phone === 'string' && raw.phone.trim()
            ? [raw.phone]
            : [];

        stores.push({
          id: typeof raw.id === 'string' && raw.id.trim() ? raw.id.trim() : `store-${index}`,
          name,
          address: raw.address || '',
          zip: raw.zip || '',
          city: raw.city || '',
          province: raw.province || '',
          country: raw.country || '',
          email: typeof raw.email === 'string' ? raw.email.trim() : '',
          phones,
          lat,
          lng,
          hours: raw.hours && typeof raw.hours === 'object' ? raw.hours : null,
        });

        return stores;
      }, []);
    }

    /* Riga sotto il nome: "Via Brera 8, 20121 Milano MI, Italy" senza doppie
     * virgole quando un pezzo manca. */
    formatAddress(store) {
      const locality = [store.zip, store.city, store.province].filter(Boolean).join(' ');
      return [store.address, locality, store.country].filter(Boolean).join(', ');
    }

    directionsHref(store) {
      const destination = this.formatAddress(store) || `${store.lat},${store.lng}`;
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
    }

    /* --- Lista ------------------------------------------------------------ */

    /*
     * Le card si creano una volta sola e restano in DOM: il filtro sui bounds fa
     * solo toggle di [hidden], che e' molto piu' economico di un re-render della
     * lista a ogni spostamento della mappa.
     */
    buildList() {
      const fragment = document.createDocumentFragment();

      this.stores.forEach((store) => {
        const card = this.elements.cardTemplate.content.firstElementChild.cloneNode(true);
        card.dataset.storeId = store.id;
        card.querySelector('[data-store-name]').textContent = store.name;
        card.querySelector('[data-store-address]').textContent = this.formatAddress(store);
        card.querySelector('[data-store-select]').addEventListener('click', () => {
          this.selectStore(store.id, { focusDetail: true });
        });

        this.entries.set(store.id, { store, card, marker: null });
        fragment.appendChild(card);
      });

      this.elements.list.appendChild(fragment);
      this.updateCount(this.stores.length);
    }

    updateCount(visible) {
      const template =
        visible === 1
          ? this.dataset.i18nCountOne
          : this.dataset.i18nCountOther;

      if (template) {
        this.elements.count.textContent = template.replace('[count]', String(visible));
      }

      this.elements.noResults.hidden = visible !== 0;
    }

    /* --- Mappa ------------------------------------------------------------ */

    async start() {
      const apiKey = this.dataset.apiKey;
      if (!apiKey) {
        this.showError();
        return;
      }

      try {
        await mapsLoader.load(apiKey);
        const { Map } = await google.maps.importLibrary('maps');

        const latitude = toFiniteNumber(this.dataset.latitude);
        const longitude = toFiniteNumber(this.dataset.longitude);
        const zoom = toFiniteNumber(this.dataset.zoom) || 6;
        const hasCustomCenter = latitude !== null && longitude !== null;

        this.map = new Map(this.elements.mapHost, {
          center: hasCustomCenter
            ? { lat: latitude, lng: longitude }
            : { lat: this.stores[0].lat, lng: this.stores[0].lng },
          zoom,
          mapId: this.dataset.mapId || undefined,
          streetViewControl: false,
          mapTypeControl: false,
        });

        /*
         * idle scatta quando la mappa si ferma, non a ogni pixel di pan come
         * bounds_changed: il debounce serve solo per le raffiche di zoom.
         */
        this.map.addListener('idle', () => {
          if (this.boundsTimer) clearTimeout(this.boundsTimer);
          this.boundsTimer = setTimeout(() => this.syncToBounds(), BOUNDS_DEBOUNCE);
        });

        if (!hasCustomCenter) this.fitAllStores();

        this.setupSearch();
        this.setupGeolocate();
        this.openFromUrl();
      } catch (error) {
        this.showError();
      }
    }

    async fitAllStores() {
      const { LatLngBounds } = await google.maps.importLibrary('core');
      const bounds = new LatLngBounds();
      this.stores.forEach((store) => bounds.extend({ lat: store.lat, lng: store.lng }));
      this.map.fitBounds(bounds);
    }

    /*
     * I marker si creano solo per i negozi dentro la vista corrente: istanziarne
     * 150+ al primo render rende la mappa lenta all'apertura. Quelli usciti dalla
     * vista vengono staccati dalla mappa ma l'istanza resta, cosi' rientrando non
     * si ricrea nulla.
     */
    async syncToBounds() {
      if (!this.map) return;

      const bounds = this.map.getBounds();
      if (!bounds) return;

      const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');
      let visible = 0;

      this.entries.forEach((entry) => {
        const position = { lat: entry.store.lat, lng: entry.store.lng };
        const inView = bounds.contains(position);

        if (inView) {
          visible += 1;
          if (!entry.marker) {
            entry.marker = new AdvancedMarkerElement({
              position,
              title: entry.store.name,
              map: this.map,
              content: this.markerContent(false, entry.store.name),
            });
            entry.marker.addListener('click', () => {
              this.selectStore(entry.store.id, { focusDetail: false });
            });
          } else if (!entry.marker.map) {
            entry.marker.map = this.map;
          }
        } else if (entry.marker) {
          entry.marker.map = null;
        }

        entry.card.hidden = !inView;
      });

      this.updateCount(visible);
    }

    /* Marker personalizzato solo se il merchant ha caricato le immagini: senza,
     * si usa il pin predefinito di Google. */
    markerContent(selected, name) {
      const src = selected ? this.dataset.markerSelected : this.dataset.marker;
      if (!src) return undefined;

      const image = document.createElement('img');
      image.src = src;
      image.alt = '';
      image.setAttribute('aria-hidden', 'true');
      image.width = 32;
      if (name) image.dataset.storeName = name;
      return image;
    }

    /* --- Ricerca e geolocalizzazione -------------------------------------- */

    /*
     * PlaceAutocompleteElement e' il widget corrente: Autocomplete e SearchBox non
     * sono piu' disponibili per i nuovi progetti. Emette gmp-select con una
     * placePrediction, da cui si ricavano viewport e coordinate.
     */
    async setupSearch() {
      if (!this.elements.search) return;

      try {
        const { PlaceAutocompleteElement } = await google.maps.importLibrary('places');
        this.autocomplete = new PlaceAutocompleteElement();

        const placeholder = this.elements.search.dataset.placeholder;
        if (placeholder) this.autocomplete.placeholder = placeholder;

        this.elements.search.appendChild(this.autocomplete);

        this.autocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
          if (!placePrediction) return;

          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['location', 'viewport'] });

          this.closeDetail({ restoreFocus: false });

          if (place.viewport) {
            this.map.fitBounds(place.viewport);
          } else if (place.location) {
            this.map.setCenter(place.location);
            this.map.setZoom(12);
          }
        });
      } catch (error) {
        /* Senza Places la mappa resta usabile: si perde solo la ricerca. */
        this.elements.search.hidden = true;
      }
    }

    setupGeolocate() {
      const button = this.elements.geolocate;
      if (!button) return;

      if (!navigator.geolocation) {
        button.hidden = true;
        return;
      }

      button.addEventListener('click', () => {
        button.setAttribute('aria-busy', 'true');

        navigator.geolocation.getCurrentPosition(
          ({ coords }) => {
            button.removeAttribute('aria-busy');
            this.closeDetail({ restoreFocus: false });
            this.map.setCenter({ lat: coords.latitude, lng: coords.longitude });
            this.map.setZoom(11);
          },
          () => {
            /* Permesso negato o posizione non disponibile: nessun messaggio
             * invasivo, la lista resta il percorso alternativo. */
            button.removeAttribute('aria-busy');
          }
        );
      });
    }

    /* --- Selezione e dettaglio -------------------------------------------- */

    selectStore(id, { focusDetail }) {
      const entry = this.entries.get(id);
      if (!entry) return;

      if (this.selectedId && this.selectedId !== id) this.deselectMarker(this.selectedId);

      this.selectedId = id;
      entry.card.dataset.selected = 'true';

      if (entry.marker) {
        const content = this.markerContent(true, entry.store.name);
        if (content) entry.marker.content = content;
      }

      this.map.setCenter({ lat: entry.store.lat, lng: entry.store.lng });
      this.map.setZoom(Math.max(this.map.getZoom() || 0, 14));

      this.renderDetail(entry.store, { focusDetail });
      this.syncUrl(id);
    }

    deselectMarker(id) {
      const entry = this.entries.get(id);
      if (!entry) return;

      delete entry.card.dataset.selected;
      if (entry.marker) {
        const content = this.markerContent(false, entry.store.name);
        if (content) entry.marker.content = content;
      }
    }

    renderDetail(store, { focusDetail }) {
      const fragment = this.elements.detailTemplate.content.cloneNode(true);

      fragment.querySelector('[data-store-name]').textContent = store.name;
      fragment.querySelector('[data-store-address]').textContent = this.formatAddress(store);

      const phoneList = fragment.querySelector('[data-store-phones]');
      if (store.phones.length) {
        store.phones.forEach((phone) => {
          const item = this.elements.phoneTemplate.content.firstElementChild.cloneNode(true);
          const link = item.querySelector('[data-store-phone-link]');
          link.href = telHref(phone);
          link.textContent = phone;
          phoneList.appendChild(item);
        });
        phoneList.hidden = false;
      }

      const email = fragment.querySelector('[data-store-email]');
      if (store.email) {
        email.href = `mailto:${store.email}`;
        email.textContent = store.email;
        email.hidden = false;
      }

      fragment.querySelector('[data-store-directions]').href = this.directionsHref(store);

      /*
       * Orari: chiave assente significa "non comunicato" e la riga non compare;
       * stringa vuota significa giorno di chiusura e la riga mostra "Closed".
       */
      const hoursBlock = fragment.querySelector('[data-store-hours]');
      if (store.hours) {
        let anyDay = false;

        DAY_CODES.forEach((code) => {
          if (!(code in store.hours)) return;

          const row = fragment.querySelector(`[data-store-hour-row="${code}"]`);
          if (!row) return;

          const value = String(store.hours[code] || '').trim();
          row.querySelector(`[data-store-hour="${code}"]`).textContent =
            value || this.dataset.i18nClosed || '';
          row.hidden = false;
          anyDay = true;
        });

        hoursBlock.hidden = !anyDay;
      }

      fragment.querySelector('[data-store-back]').addEventListener('click', () => {
        this.closeDetail({ restoreFocus: true });
      });

      this.elements.detail.replaceChildren(fragment);
      this.elements.detail.hidden = false;
      this.elements.results.hidden = true;
      this.detailOpen = true;

      if (focusDetail) {
        this.lastFocused = document.activeElement;
        this.elements.detail.querySelector('[data-store-name]').focus();
      }
    }

    closeDetail({ restoreFocus }) {
      if (!this.detailOpen) return;

      this.elements.detail.hidden = true;
      this.elements.detail.replaceChildren();
      this.elements.results.hidden = false;
      this.detailOpen = false;

      if (this.selectedId) {
        const entry = this.entries.get(this.selectedId);
        this.deselectMarker(this.selectedId);

        /* Il focus torna sulla card da cui si e' partiti, non all'inizio della
         * lista: senza questo, con 150 negozi si perde il punto di lettura. */
        if (restoreFocus) {
          const target = entry && entry.card.querySelector('[data-store-select]');
          if (target && !entry.card.hidden) {
            target.focus();
          } else if (this.lastFocused && this.lastFocused.isConnected) {
            this.lastFocused.focus();
          }
        }

        this.selectedId = null;
      }

      this.syncUrl(null);
    }

    handleKeydown(event) {
      if (event.key !== 'Escape' || !this.detailOpen) return;
      this.closeDetail({ restoreFocus: true });
    }

    /* --- Deep link -------------------------------------------------------- */

    /* ?store=<id> apre un negozio specifico: serve al customer care per mandare
     * il link di un punto vendita. */
    openFromUrl() {
      const requested = new URLSearchParams(window.location.search).get('store');
      if (!requested || !this.entries.has(requested)) return;
      this.selectStore(requested, { focusDetail: false });
    }

    syncUrl(id) {
      const url = new URL(window.location.href);
      if (id) {
        url.searchParams.set('store', id);
      } else {
        url.searchParams.delete('store');
      }
      window.history.replaceState({}, '', url);
    }

    /* --- Errori ----------------------------------------------------------- */

    showError() {
      if (this.elements.error) this.elements.error.hidden = false;
      if (this.elements.mapHost) this.elements.mapHost.hidden = true;
      if (this.elements.search) this.elements.search.hidden = true;
      if (this.elements.geolocate) this.elements.geolocate.hidden = true;
      if (this.elements.count) this.elements.count.textContent = '';
    }
  }

  customElements.define('store-locator', StoreLocator);
}
